import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SkillsManager } from '../skills/SkillsManager';
import { fetchProjectRepos } from '../../lib/api/projects';
import {
  deleteProjectSkill,
  listProjectSkills,
  previewProjectSkill,
  toggleProjectSkill,
  uploadProjectSkill,
  type Skill,
  type SkillListItem,
  type SkillPreview,
} from '../../lib/api/skills';

/**
 * Project-scope BYOS surface. Mounted as a tab under
 * /projects/:id — adds, removes, and edits skills that every repo in
 * the project inherits at agent launch.
 *
 * UX-04: also surfaces the project's repos with a link to each one's
 * override page (`/repos/:org/:repo/skills`) — previously the only way
 * in was the `/repos` landing page, which most operators never visit.
 */
export const SkillsTab: React.FC<{ projectId: string }> = ({ projectId }) => (
  <div className="space-y-6">
    <SkillsManager
      scopeKey={['project', projectId]}
      list={() => listProjectSkills(projectId)}
      upload={(file: File, opts: { name?: string; description?: string }): Promise<Skill> =>
        uploadProjectSkill(projectId, { file, name: opts.name, description: opts.description })
      }
      preview={(item: SkillListItem): Promise<SkillPreview> =>
        previewProjectSkill(projectId, item.slug)
      }
      toggle={(item: SkillListItem, active: boolean): Promise<Skill> =>
        toggleProjectSkill(projectId, item.slug, active)
      }
      remove={(item: SkillListItem): Promise<void> =>
        deleteProjectSkill(projectId, item.slug)
      }
    />
    <RepoOverridesList projectId={projectId} />
  </div>
);

const RepoOverridesList: React.FC<{ projectId: string }> = ({ projectId }) => {
  const reposQ = useQuery({
    queryKey: ['project', projectId, 'repos'],
    queryFn: () => fetchProjectRepos(projectId),
    enabled: projectId.length > 0,
  });

  if (reposQ.isLoading || reposQ.isError || (reposQ.data?.length ?? 0) === 0) return null;

  return (
    <div className="space-y-2">
      <header className="text-xs font-medium text-slate-500 uppercase tracking-wide">
        Repo overrides
      </header>
      <p className="text-xs text-slate-500">
        Repo entries override a project skill of the same name for that repo only.
      </p>
      <div className="border rounded divide-y" data-testid="skills-repo-overrides">
        {(reposQ.data ?? []).map((repo) => {
          const [org, name] = repo.fullName.split('/');
          return (
            <div key={repo.fullName} className="flex items-center justify-between px-3 py-2 text-sm">
              <span className="font-mono text-xs text-slate-700">{repo.fullName}</span>
              {org && name ? (
                <Link
                  to={`/repos/${encodeURIComponent(org)}/${encodeURIComponent(name)}/skills`}
                  className="text-xs text-primary-500 hover:text-primary-600 hover:underline whitespace-nowrap"
                >
                  Manage →
                </Link>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SkillsTab;
